import {
    BugReport as BugIcon,
    NewReleases as FeatureIcon,
    Security as SecurityIcon,
    TrendingUp as ImprovementIcon,
    Update as UpdateIcon,
} from '@mui/icons-material';
import { Alert, Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
} from '@mui/lab';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import { appRelease } from '../domain/appRelease';
import useQuery from '../hooks/useQuery';

interface ChangelogEntry {
    id: number;
    version: string;
    title: string;
    description: string;
    category: string;
    releaseDate: string;
}

const categoryConfig: Record<
    string,
    {
        color: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary';
        icon: React.ReactElement;
        labelKey: string;
    }
> = {
    feature: { color: 'success', icon: <FeatureIcon />, labelKey: 'changelog.categoryFeature' },
    bugfix: { color: 'error', icon: <BugIcon />, labelKey: 'changelog.categoryBugfix' },
    improvement: {
        color: 'info',
        icon: <ImprovementIcon />,
        labelKey: 'changelog.categoryImprovement',
    },
    security: { color: 'warning', icon: <SecurityIcon />, labelKey: 'changelog.categorySecurity' },
};

const ChangelogPage = () => {
    const { t, i18n } = useTranslation();

    const appVersion = appRelease.version;

    const { data, loading, error } = useQuery<{ entries: ChangelogEntry[] }>({
        path: '/changelog',
        params: { lang: i18n.language },
    });

    const allEntries = useMemo(() => {
        const entries = data?.entries ?? [];
        return [...entries].sort((a, b) => {
            const dateDiff = new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
            if (dateDiff !== 0) return dateDiff;
            const versionA = parseInt(a.version.substring(a.version.lastIndexOf('.') + 1), 10);
            const versionB = parseInt(b.version.substring(b.version.lastIndexOf('.') + 1), 10);
            return versionB - versionA;
        });
    }, [data?.entries]);

    const [activeFilter, setActiveFilter] = useState<string | null>(null);

    const toggleFilter = useCallback((category: string) => {
        setActiveFilter(prev => (prev === category ? null : category));
    }, []);

    const entries = useMemo(
        () =>
            activeFilter === null
                ? allEntries
                : allEntries.filter(entry => entry.category === activeFilter),
        [allEntries, activeFilter],
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{t('changelog.error')}</Alert>
            </Box>
        );
    }

    return (
        <Box p={3} maxWidth="900px" mx="auto">
            <PageHeaderBanner
                icon={<UpdateIcon />}
                title={t('changelog.title')}
                subtitle={t('changelog.subtitle')}
                accentColor="#7c3aed"
                actions={
                    appVersion && (
                        <Chip
                            label={`${t('changelog.currentVersion')}: v${appVersion}`}
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                        />
                    )
                }
            />

            <Box display="flex" justifyContent="center" gap={1} mb={4} flexWrap="wrap">
                {Object.entries(categoryConfig).map(([key, config]) => (
                    <Chip
                        key={key}
                        icon={config.icon}
                        label={t(config.labelKey)}
                        color={config.color}
                        variant={activeFilter === key ? 'filled' : 'outlined'}
                        size="small"
                        onClick={() => toggleFilter(key)}
                        sx={{ cursor: 'pointer' }}
                    />
                ))}
            </Box>

            {entries.length === 0 ? (
                <Box textAlign="center" py={6}>
                    <Typography color="text.secondary">{t('changelog.noEntries')}</Typography>
                </Box>
            ) : (
                <Timeline position="alternate">
                    {entries.map((entry, index) => {
                        const config = categoryConfig[entry.category] ?? categoryConfig.feature;
                        return (
                            <TimelineItem key={entry.id}>
                                <TimelineOppositeContent
                                    sx={{ m: 'auto 0' }}
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {new Date(entry.releaseDate).toLocaleDateString()}
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    <TimelineConnector
                                        sx={{
                                            bgcolor: index === 0 ? 'transparent' : 'grey.300',
                                        }}
                                    />
                                    <TimelineDot color={config.color}>{config.icon}</TimelineDot>
                                    <TimelineConnector
                                        sx={{
                                            bgcolor:
                                                index === entries.length - 1
                                                    ? 'transparent'
                                                    : 'grey.300',
                                        }}
                                    />
                                </TimelineSeparator>
                                <TimelineContent sx={{ py: 2, px: 2 }}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 2.5,
                                            borderLeft: 4,
                                            borderColor: `${config.color}.main`,
                                            transition: 'box-shadow 0.2s',
                                            '&:hover': { boxShadow: 6 },
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                            <Chip
                                                label={`v${entry.version}`}
                                                size="small"
                                                color="primary"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                            <Chip
                                                label={t(config.labelKey)}
                                                size="small"
                                                color={config.color}
                                                variant="outlined"
                                            />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="bold" mt={1}>
                                            {entry.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                                            {entry.description}
                                        </Typography>
                                    </Paper>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    })}
                </Timeline>
            )}
        </Box>
    );
};

export default ChangelogPage;
